import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { Disclaimer } from "../components/Disclaimer";
import { PaymentBadge, ReviewBadge, CaseStatusBadge } from "../components/StatusBadges";
import StateTransitionPanel from "../components/StateTransitionPanel";
import { BudgetHistory } from "../components/n1/BudgetHistory";

export default function ReviewerCaseDetailPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reviewer/cases/${id}`);
      setDoc(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="reviewer-case-detail-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to="/reviewer" className="text-sm text-gray-500 hover:text-primary">← Volver a la bandeja</Link>
        </div>

        {loading && <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando caso…</div>}

        {!loading && error && (
          <div data-testid="reviewer-case-detail-error" className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm">{error}</div>
        )}

        {!loading && doc && (
          <>
            <div className="mb-8 pb-6 border-b border-gray-200">
              <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Caso asignado · {doc.id.slice(0, 8)}</p>
              <h1 data-testid="reviewer-case-detail-title" className="font-serif text-3xl text-gray-900 mb-4">{doc.title}</h1>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <CaseStatusBadge value={doc.status} />
                <PaymentBadge value={doc.payment_status} />
                <ReviewBadge value={doc.review_status} />
                <span className="text-gray-500 ml-2">Asignado el {formatDate(doc.assigned_at)}</span>
              </div>
            </div>

            <div className="mb-6"><BudgetHistory caseId={id} /></div>

            {/* Transiciones contextuales (rol reviewer) */}
            <div className="mb-6 border border-gray-200 bg-gray-50 rounded-sm p-4">
              <StateTransitionPanel
                caseId={id}
                currentState={doc.state}
                onTransitioned={() => load()}
                endpoint="/reviewer/cases"
              />
            </div>

            <div className="border border-gray-200 bg-white">
              <dl className="divide-y divide-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Dueño del caso</dt>
                  <dd className="md:col-span-2 text-sm text-gray-800">
                    {doc.user_name || "—"}<br />
                    <span className="text-xs text-gray-500">{doc.user_email}</span>
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Objeto contractual</dt>
                  <dd className="md:col-span-2 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{doc.objeto_contractual}</dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Valor estimado</dt>
                  <dd className="md:col-span-2 font-serif text-xl text-gray-900">{formatCOP(doc.valor_estimado_cop)}</dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-6">
                  <dt className="text-xs uppercase tracking-widest text-gray-500">Creado</dt>
                  <dd className="md:col-span-2 text-sm text-gray-800">{formatDate(doc.created_at)}</dd>
                </div>
              </dl>
            </div>

            <div className="mt-6 border border-gray-200 bg-gray-50 p-5 text-sm text-gray-700">
              <p className="font-medium text-gray-900 mb-1">Vista de revisor (solo lectura).</p>
              <p>
                Los botones para aprobar o rechazar Diagnósticos y Kits Documentales se habilitarán en próximas fases,
                cuando se active el flujo de pago y de generación documental.
              </p>
            </div>

            {/* Link a revisión Kit Documental N2 si aplica */}
            {["nivel2_needs_human_review", "nivel2_revision_requested", "nivel2_delivered"].includes(doc.state) && (
              <div className="mt-4 border border-primary/30 bg-primary/[0.03] p-5">
                <p className="text-[11px] uppercase tracking-widest text-primary font-medium mb-1">
                  Kit Documental Nivel 2
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  Este caso tiene un Kit Documental que requiere tu revisión.
                </p>
                <Link
                  to={`/reviewer/cases/${doc.id}/kit-documental`}
                  className="inline-block rounded-sm bg-primary text-white px-4 py-2 text-sm hover:bg-primary/90"
                  data-testid="link-to-n2-review"
                >
                  Abrir revisión del Kit Documental →
                </Link>
              </div>
            )}

            <div className="mt-8">
              <Disclaimer dataTestId="reviewer-case-detail-disclaimer" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
